import React, { useState, useEffect } from 'react';
import "../../css/Subjects.css";
import Loader from "../../components/Loader";
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { FaBook, FaEdit, FaTrash, FaSearch, FaTimes } from 'react-icons/fa';

function Subjects() {
    const [subjects, setSubjects] = useState([]);
    const [filteredSubjects, setFilteredSubjects] = useState([]);
    const [newSubject, setNewSubject] = useState({
        name: '',
        code: '',
        department: '',
        credits: '',
        description: ''
    });
    const [isLoading, setIsLoading] = useState(false);
    const [isFormVisible, setIsFormVisible] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [subjectToDelete, setSubjectToDelete] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        fetchSubjects();
    }, []);

    const fetchSubjects = async () => {
        const token = localStorage.getItem("token");
        if (!token) {
            toast.error("Unauthorized! Please log in again.");
            return;
        }

        setIsLoading(true);

        try {
            const response = await fetch("https://schoolpro-api.uniums.com/api/subjects", {
                headers: {
                    "Authorization": `Bearer ${token}`
                }
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || "Failed to fetch subjects");
            }

            const data = await response.json();
            setSubjects(data);
            setFilteredSubjects(data);
        } catch (error) {
            console.error("Error fetching subjects:", error);
            toast.error(`Error: ${error.message}`);
        } finally {
            setIsLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setNewSubject({
            ...newSubject,
            [name]: value
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);

        const token = localStorage.getItem("token");

        try {
            let response;

            if (isEditing) {
                // Update existing subject
                response = await fetch(`https://schoolpro-api.uniums.com/api/subjects/${editingId}`, {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    },
                    body: JSON.stringify(newSubject)
                });
            } else {
                // Add new subject
                response = await fetch("https://schoolpro-api.uniums.com/api/subjects/add", {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    },
                    body: JSON.stringify(newSubject)
                });
            }

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || (isEditing ? "Failed to update subject" : "Failed to add subject"));
            }

            const data = await response.json();

            if (isEditing) {
                toast.success("Subject updated successfully!");
                setIsEditing(false);
                setEditingId(null);
            } else {
                toast.success("Subject added successfully!");
            }

            // Refresh the subject list
            fetchSubjects();

            // Reset form
            setNewSubject({
                name: '',
                code: '',
                department: '',
                credits: '',
                description: ''
            });
            setIsFormVisible(false);
        } catch (error) {
            console.error("Error:", error);
            toast.error(`Error: ${error.message}`);
        } finally {
            setIsLoading(false);
        }
    };

    const handleEdit = (subject) => {
        setNewSubject({
            name: subject.name,
            code: subject.code,
            department: subject.department || '',
            credits: subject.credits || '',
            description: subject.description || ''
        });
        setEditingId(subject._id);
        setIsEditing(true);
        setIsFormVisible(true);
    };

    const confirmDelete = (subject) => {
        setSubjectToDelete(subject);
        setShowDeleteModal(true);
    };

    const handleDelete = async () => {
        if (subjectToDelete) {
            setIsLoading(true);

            const token = localStorage.getItem("token");

            try {
                const response = await fetch(`https://schoolpro-api.uniums.com/api/subjects/${subjectToDelete._id}`, {
                    method: 'DELETE',
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                });

                if (!response.ok) {
                    const errorData = await response.json();
                    throw new Error(errorData.message || "Failed to delete subject");
                }

                toast.success("Subject deleted successfully!");
                fetchSubjects();
            } catch (error) {
                console.error("Error deleting subject:", error);
                toast.error(`Error: ${error.message}`);
            } finally {
                setIsLoading(false);
                setShowDeleteModal(false);
                setSubjectToDelete(null);
            }
        }
    };

    const cancelDelete = () => {
        setShowDeleteModal(false);
        setSubjectToDelete(null);
    };

    const cancelEdit = () => {
        setNewSubject({
            name: '',
            code: '',
            department: '',
            credits: '',
            description: ''
        });
        setIsEditing(false);
        setEditingId(null);
        setIsFormVisible(false);
    };

    // Handle search
    useEffect(() => {
        if (searchTerm.trim() === '') {
            setFilteredSubjects(subjects);
        } else {
            const results = subjects.filter(subj =>
                (subj.name?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
                (subj.code?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
                (subj.department?.toLowerCase() || '').includes(searchTerm.toLowerCase())
            );
            setFilteredSubjects(results);
        }
    }, [searchTerm, subjects]);

    const handleSearch = (e) => {
        setSearchTerm(e.target.value);
    };

    return (
        <div className="subjects-container">
            <ToastContainer />

            {isLoading && <Loader />}

            <div className="subjects-header">
                <div className="subjects-title-container">
                    <h1 className="subjects-title">
                        <FaBook className="subjects-icon" /> Subject Management
                    </h1>
                </div>

                <div className="subjects-controls">
                    <div className="subjects-search-container">
                        <FaSearch className="subjects-search-icon" />
                        <input
                            type="text"
                            className="subjects-search-input"
                            placeholder="Search subjects..."
                            value={searchTerm}
                            onChange={handleSearch}
                        />
                    </div>

                    <button
                        className="subjects-btn subjects-btn-primary"
                        onClick={() => {
                            if (isFormVisible && isEditing) {
                                cancelEdit();
                            } else {
                                setIsFormVisible(!isFormVisible);
                            }
                        }}
                    >
                        {isFormVisible ? 'Cancel' : 'Add New Subject'}
                    </button>
                </div>

                <div className="subjects-stats">
                    <div className="subjects-stat-box">
                        <span className="subjects-stat-number">{filteredSubjects.length}</span>
                        <span className="subjects-stat-label">Subjects</span>
                    </div>
                </div>
            </div>

            {isFormVisible && (
                <div className="subjects-form-container">
                    <form className="subjects-form" onSubmit={handleSubmit}>
                        <h2 className="subjects-form-title">
                            {isEditing ? 'Edit Subject' : 'Add New Subject'}
                        </h2>

                        <div className="subjects-form-grid">
                            <div className="subjects-form-group">
                                <label className="subjects-label">Subject Name*</label>
                                <input
                                    type="text"
                                    name="name"
                                    value={newSubject.name}
                                    onChange={handleInputChange}
                                    className="subjects-input"
                                    placeholder="e.g. Mathematics"
                                    required
                                />
                            </div>

                            <div className="subjects-form-group">
                                <label className="subjects-label">Subject Code*</label>
                                <input
                                    type="text"
                                    name="code"
                                    value={newSubject.code}
                                    onChange={handleInputChange}
                                    className="subjects-input"
                                    placeholder="e.g. MATH101"
                                    required
                                />
                            </div>

                            <div className="subjects-form-group">
                                <label className="subjects-label">Department</label>
                                <input
                                    type="text"
                                    name="department"
                                    value={newSubject.department}
                                    onChange={handleInputChange}
                                    className="subjects-input"
                                    placeholder="e.g. Science"
                                />
                            </div>

                            <div className="subjects-form-group">
                                <label className="subjects-label">Credits</label>
                                <input
                                    type="number"
                                    name="credits"
                                    value={newSubject.credits}
                                    onChange={handleInputChange}
                                    className="subjects-input"
                                    placeholder="e.g. 4"
                                />
                            </div>
                        </div>

                        <div className="subjects-form-group full-width">
                            <label className="subjects-label">Description</label>
                            <textarea
                                name="description"
                                value={newSubject.description}
                                onChange={handleInputChange}
                                className="subjects-textarea"
                                placeholder="Brief description of the subject..."
                                rows="3"
                            ></textarea>
                        </div>

                        <div className="subjects-form-actions">
                            <button type="submit" className="subjects-btn subjects-btn-success">
                                {isEditing ? 'Update Subject' : 'Add Subject'}
                            </button>
                            <button
                                type="button"
                                className="subjects-btn subjects-btn-secondary"
                                onClick={cancelEdit}
                            >
                                Cancel
                            </button>
                        </div>
                    </form>
                </div>
            )}

            <div className="subjects-list-container">
                <h2 className="subjects-section-title">All Subjects</h2>

                {filteredSubjects.length === 0 && !isLoading ? (
                    <div className="subjects-empty-state">
                        {searchTerm ? (
                            <>
                                <p>No subjects found matching your search criteria.</p>
                                <button className="subjects-reset-search" onClick={() => setSearchTerm('')}>
                                    Clear Search
                                </button>
                            </>
                        ) : (
                            <p>No subjects have been added yet. Click "Add New Subject" to create your first subject.</p>
                        )}
                    </div>
                ) : (
                    <div className="subjects-grid">
                        {filteredSubjects.map(subject => (
                            <div key={subject._id} className="subjects-card">
                                <div className="subjects-card-header">
                                    <h3 className="subjects-card-title">{subject.name}</h3>
                                    <span className="subjects-code-badge">{subject.code}</span>
                                </div>
                                <div className="subjects-card-body">
                                    {subject.department && (
                                        <p><strong>Department:</strong> {subject.department}</p>
                                    )}
                                    {subject.credits && (
                                        <p><strong>Credits:</strong> {subject.credits}</p>
                                    )}
                                    {subject.description && (
                                        <p className="subjects-description">{subject.description}</p>
                                    )}
                                </div>
                                <div className="subjects-card-footer">
                                    <button
                                        className="subjects-btn subjects-btn-edit subjects-btn-sm"
                                        onClick={() => handleEdit(subject)}
                                    >
                                        <FaEdit /> Edit
                                    </button>
                                    <button
                                        className="subjects-btn subjects-btn-danger subjects-btn-sm"
                                        onClick={() => confirmDelete(subject)}
                                    >
                                        <FaTrash /> Delete
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Delete Confirmation Modal */}
            {showDeleteModal && (
                <div className="subjects-modal-overlay">
                    <div className="subjects-modal">
                        <div className="subjects-modal-header">
                            <h2 className="subjects-modal-title">Delete Subject</h2>
                            <button className="subjects-close-btn" onClick={cancelDelete}>
                                <FaTimes />
                            </button>
                        </div>
                        <div className="subjects-modal-body">
                            <div className="subjects-delete-warning">
                                <FaTrash className="subjects-delete-icon" />
                                <p>Are you sure you want to delete subject <strong>{subjectToDelete?.name}</strong>?</p>
                                <p className="subjects-warning-text">This action cannot be undone.</p>
                            </div>
                        </div>
                        <div className="subjects-modal-footer">
                            <button className="subjects-btn subjects-btn-secondary" onClick={cancelDelete}>
                                Cancel
                            </button>
                            <button className="subjects-btn subjects-btn-danger" onClick={handleDelete}>
                                Delete Subject
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default Subjects;